import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, Save, FolderOpen, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { EditorPanel } from './EditorPanel';
import { PreviewPanel } from './PreviewPanel';
import { useEmailEditor } from './useEmailEditor';
import { emailTemplatesApi, type SavedEmailTemplate } from '../../api/endpoints/emailTemplates';
import type { EmailTemplateEditorProps, EmailEditorState, EmailTemplateType, ColorConfig } from './types';

export function EmailTemplateEditor({
  initialState,
  context,
  onSubmit,
  onCancel,
  isSubmitting,
  allowedTemplates,
}: EmailTemplateEditorProps) {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const queryClient = useQueryClient();
  const editor = useEmailEditor({ initialState, context });

  // Save modal state
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');

  // Load modal state
  const [showLoad, setShowLoad] = useState(false);

  // Fetch saved templates
  const { data: savedData } = useQuery({
    queryKey: ['email-templates', storeId],
    queryFn: () => emailTemplatesApi.list(storeId!),
    enabled: !!storeId,
  });

  const saveMutation = useMutation({
    mutationFn: (name: string) =>
      emailTemplatesApi.save(storeId!, {
        name,
        ...editor.editorState,
      }),
    onSuccess: () => {
      setShowSave(false);
      setSaveName('');
      queryClient.invalidateQueries({ queryKey: ['email-templates', storeId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (templateId: string) => emailTemplatesApi.delete(storeId!, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates', storeId] });
    },
  });

  const handleLoadTemplate = (saved: SavedEmailTemplate) => {
    editor.selectTemplate((saved.templateType || 'generic') as EmailTemplateType);
    // Apply saved customizations on top of template defaults
    if (saved.subject) editor.setSubject(saved.subject);
    if (saved.greeting) editor.setGreeting(saved.greeting);
    if (saved.bodyHtml) editor.setBodyHtml(saved.bodyHtml);
    if (saved.ctaText) editor.setCtaText(saved.ctaText);
    if (saved.ctaUrl) editor.setCtaUrl(saved.ctaUrl);
    if (saved.colors) {
      Object.entries(saved.colors).forEach(([k, v]) => {
        editor.setColor(k as keyof ColorConfig, v as string);
      });
    }
    setShowLoad(false);
  };

  const savedTemplates = savedData?.templates || [];

  return (
    <div className="flex flex-col" style={{ height: '70vh', maxHeight: '720px' }}>
      {/* Split pane: editor + preview */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(300px,1fr)_minmax(340px,1.2fr)] min-h-0">
        {/* Editor panel */}
        <div className="border-e border-border-default overflow-hidden">
          <EditorPanel
            templateType={editor.templateType}
            subject={editor.subject}
            greeting={editor.greeting}
            bodyHtml={editor.bodyHtml}
            ctaText={editor.ctaText}
            ctaUrl={editor.ctaUrl}
            colors={editor.colors}
            selectTemplate={editor.selectTemplate}
            setSubject={editor.setSubject}
            setGreeting={editor.setGreeting}
            setBodyHtml={editor.setBodyHtml}
            setCtaText={editor.setCtaText}
            setCtaUrl={editor.setCtaUrl}
            setColor={editor.setColor}
            allowedTemplates={allowedTemplates}
          />
        </div>

        {/* Preview panel - always visible */}
        <div className="hidden lg:block p-3 overflow-hidden">
          <PreviewPanel
            html={editor.previewHtml}
            recipientEmail={context?.customerEmail}
            subject={editor.subject}
          />
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center gap-3 border-t border-border-default px-4 py-3 bg-bg-secondary">
        {/* Left: load/save actions */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowLoad(true)}>
            <FolderOpen className="h-3.5 w-3.5" />
            {t('emailEditor.loadSaved')}
            {savedTemplates.length > 0 && (
              <span className="ms-1 rounded-full bg-accent/15 px-1.5 text-xs font-semibold text-accent">
                {savedTemplates.length}
              </span>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setSaveName(''); setShowSave(true); }}>
            <Save className="h-3.5 w-3.5" />
            {t('emailEditor.saveTemplate')}
          </Button>
        </div>

        {/* Right: cancel/send */}
        <div className="flex-1" />
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button onClick={() => onSubmit(editor.editorState)} isLoading={isSubmitting}>
          <Send className="h-4 w-4" />
          {t('emailEditor.send')}
        </Button>
      </div>

      {/* Save Template Modal */}
      <Modal
        isOpen={showSave}
        onClose={() => setShowSave(false)}
        title={t('emailEditor.saveTemplate')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowSave(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={() => saveName.trim() && saveMutation.mutate(saveName.trim())}
              isLoading={saveMutation.isPending}
              disabled={!saveName.trim()}
            >
              <Save className="h-4 w-4" />
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Input
          label={t('emailEditor.templateName')}
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          placeholder={t('emailEditor.templateNamePlaceholder')}
          autoFocus
        />
      </Modal>

      {/* Load Saved Templates Modal */}
      <Modal
        isOpen={showLoad}
        onClose={() => setShowLoad(false)}
        title={t('emailEditor.loadSaved')}
        size="md"
      >
        {savedTemplates.length === 0 ? (
          <div className="py-8 text-center text-text-muted">
            <FolderOpen className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">{t('emailEditor.noSaved')}</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {savedTemplates.map((saved) => (
              <div
                key={saved._id}
                className="flex items-center gap-3 rounded-lg border border-border-default bg-bg-primary p-3 hover:border-accent/40 transition-colors"
              >
                <div
                  className="h-8 w-8 shrink-0 rounded-lg"
                  style={{ backgroundColor: saved.colors?.brandColor || '#4f46e5' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{saved.name}</p>
                  <p className="text-xs text-text-muted truncate">{saved.subject || saved.templateType}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleLoadTemplate(saved)}>
                  {t('emailEditor.use')}
                </Button>
                <button
                  type="button"
                  onClick={() => deleteMutation.mutate(saved._id)}
                  className="rounded p-1.5 text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
